import * as RouteHelper from "@utils/paths";
import { OrgSectionVisibilityModel } from "catchment-common/entities/organisationDashboard";
import { findFramework } from "catchment-common/interfaces/framework";
import { OrganisationFrameworkSummaryLineItem } from "catchment-common/interfaces/projectFramework";
import { NavItemListType, NavItemType } from "types";
import Icon from "@ui/atoms/Icon/Icon";
import { Monogram } from "@ui/atoms/Monogram/Monogram";
import {
  EMISSIONS_TAB_TITLE,
  ENERGY_TAB_TITLE,
  MATERIALS_TAB_TITLE,
  TRANSPORT_TAB_TITLE,
  WASTE_TAB_TITLE,
  WATER_TAB_TITLE
} from "@domains/environmental/constants/environmental";

/**
 * EnviroTrack's children at org scope. Unlike the project sidebar, there is no
 * per-project commitment to filter by here — an organisation has no single
 * answer to "which categories are committed" — so all six always show,
 * matching how `organisationTabs()` always rendered all six as tabs before
 * this split (org-level visibility is the section-wide
 * `isEnvironmentalVisible` flag only, same as today).
 */
const getEnvironmentalChildren = (): NavItemType[] => [
  {
    icon: <Icon name="emission" />,
    id: "insights-emissions",
    title: EMISSIONS_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsEmissions
  },
  {
    icon: <Icon name="energy" />,
    id: "insights-energy",
    title: ENERGY_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsEnergy
  },
  {
    icon: <Icon name="water" />,
    id: "insights-water",
    title: WATER_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsWater
  },
  {
    icon: <Icon name="delete" />,
    id: "insights-waste",
    title: WASTE_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsWaste
  },
  {
    icon: <Icon name="material" />,
    id: "insights-materials",
    title: MATERIALS_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsMaterials
  },
  {
    icon: <Icon name="transport" />,
    id: "insights-transport",
    title: TRANSPORT_TAB_TITLE,
    type: "item",
    url: RouteHelper.orgInsightsTransport
  }
];

const getGovAssureChildren = (): NavItemType[] => [
  {
    icon: <Icon name="docs" />,
    id: "insights-documents",
    title: "Documents",
    type: "item",
    url: RouteHelper.orgInsightsDocuments
  },
  {
    icon: <Icon name="file-check" />,
    id: "insights-forms",
    title: "Forms",
    type: "item",
    url: RouteHelper.orgInsightsForms
  },
  {
    icon: <Icon name="library" />,
    id: "insights-registers",
    title: "Registers",
    type: "item",
    url: RouteHelper.orgInsightsRegisters
  },
  {
    icon: <Icon name="list" />,
    id: "insights-surveys",
    title: "Surveys",
    type: "item",
    url: RouteHelper.orgInsightsSurveys
  }
];

/**
 * The Insights rows, filtered to the sections the org actually has commitments
 * for — resolved server-side by `getSectionVisibility`, which applies the same
 * both-sides rule as the project sidebar's `getSectionVisibility` util.
 *
 * `undefined` means the flags are still in flight, and the whole group is left
 * out: showing all five and then removing some reads as a fault, where a beat of
 * absence reads as loading. An org with no commitments anywhere gets no Insights
 * group at all — a childless group is dropped caption and all (`rendersRows`).
 */
const getInsightsChildren = (
  visibility?: OrgSectionVisibilityModel
): NavItemType[] => {
  if (!visibility) {
    return [];
  }

  const children: NavItemType[] = [];

  const socialProChildren: NavItemType[] = [
    ...(visibility.isLabourHoursVisible
      ? [
          {
            icon: <Icon name="pending" />,
            id: "insights-labour-hours",
            title: "Participation",
            type: "item",
            url: RouteHelper.orgInsightsLabourHours
          }
        ]
      : []),
    ...(visibility.isSocialSpendVisible
      ? [
          {
            icon: <Icon name="dollar" />,
            id: "insights-social-spend",
            title: "Certified Spend",
            type: "item",
            url: RouteHelper.orgInsightsSocialSpend
          }
        ]
      : []),
    ...(visibility.isLocalContentVisible
      ? [
          {
            icon: <Icon name="map-marker" />,
            id: "insights-local-content",
            title: "Content Origin",
            type: "item",
            url: RouteHelper.orgInsightsLocalContent
          }
        ]
      : [])
  ];

  if (socialProChildren.length > 0) {
    children.push({
      children: socialProChildren,
      icon: <Icon name="webhook" />,
      id: "insights-social-pro",
      title: "SocialPro",
      type: "item",
      url: socialProChildren[0].url
    });
  }

  if (visibility.isEnvironmentalVisible) {
    children.push({
      children: getEnvironmentalChildren(),
      icon: <Icon name="environmental" />,
      id: "insights-environmental",
      title: "EnviroTrack",
      type: "item",
      url: RouteHelper.orgInsightsEmissions
    });
  }

  if (visibility.isDocumentsVisible) {
    children.push({
      children: getGovAssureChildren(),
      icon: <Icon name="governance" />,
      // Not `insights-documents`: that is the Documents CHILD's id, and a
      // parent wearing it stole the highlight from the row beneath it.
      id: "insights-govassure",
      title: "GovAssure",
      type: "item",
      url: RouteHelper.orgInsightsDocuments
    });
  }

  return children;
};

/**
 * The Compliance rows: one per framework the organisation's projects REPORT
 * AGAINST — catalogue entries and adopted templates alike, keyed by the
 * framework id a project stores (`custom:<familyId>` / `VIC-4`). The same
 * shape the PROJECT sidebar's Compliance dropdown has: a toggle row over one
 * child per framework, each wearing its abbreviation as a monogram (see
 * `getComplianceChildren` in the project menu for why a monogram and not a
 * shared glyph).
 *
 * Deliberately NOT unioned with the org's authored templates: a template
 * nothing adopts (or that every project has since dropped) has no compliance
 * to show, and listing it rendered a framework of empty states. Compliance is
 * the view of what projects answer; the Library is where authored frameworks
 * live either way. An adopted template appears here through its
 * `project_framework` rows, which carry the only name and abbreviation a
 * project ever has for it.
 */
const getComplianceChildren = (
  frameworksInUse: OrganisationFrameworkSummaryLineItem[]
): NavItemType[] =>
  frameworksInUse.map((framework) => ({
    icon: (() => {
      // Catalogue first, then the row's own — a built-in stores no
      // abbreviation of its own, same resolution `frameworkAbbreviationFor`
      // does for a project row.
      const abbreviation =
        findFramework(
          framework.frameworkId,
          framework.country ?? "",
          framework.state
        )?.frameworkAbbreviation ?? framework.frameworkAbbreviation;
      return abbreviation ? (
        <Monogram label={abbreviation} />
      ) : (
        <Icon name="file-check" />
      );
    })(),
    id: `org-compliance-${framework.frameworkId}`,
    title: framework.frameworkName,
    type: "item",
    url: RouteHelper.organisationCompliance(framework.frameworkId)
  }));

/**
 * "Overview": where the organisation itself is, before any analysis of it —
 * its dashboard, its projects and its people. Dashboard leads, because it is
 * where the nav's home link lands.
 *
 * The group's id stays `manage`: it is what `resolveSelectedItem` and the
 * menu's skeleton keys off, and nothing user-facing reads it.
 */
const getManageNav = (projectCount?: number): NavItemType => {
  const nav = {
    children: [
      {
        dataTestId: "organisation-dashboard",
        icon: <Icon name="home" />,
        id: "organisation-dashboard",
        title: "Dashboard",
        type: "item",
        url: RouteHelper.organisationDashboard
      },
      {
        // How many projects the organisation has, shown the way the design
        // does: a plain count, not a pill. Undefined while the list loads,
        // so the row simply has no count rather than a "0" that then changes.
        badge: projectCount,
        icon: <Icon name="folders" />,
        id: "projects",
        title: "Projects",
        type: "item",
        url: RouteHelper.projects
      },
      {
        breadcrumbs: false,
        dataTestId: "employees",
        icon: <Icon name="users" />,
        id: "employees",
        title: "Employees",
        type: "item",
        url: RouteHelper.employees
      }
    ],
    id: "manage",
    title: "Overview",
    type: "group"
  };

  return nav;
};

const getInsightsNav = (
  visibility?: OrgSectionVisibilityModel,
  frameworksInUse: OrganisationFrameworkSummaryLineItem[] = [],
  isContractor = false
): NavItemType => {
  const children = getInsightsChildren(visibility);

  // Compliance is the contractor's view — the frameworks ITS projects answer.
  // Gated on the same flag as project set-up.
  const complianceChildren = isContractor
    ? getComplianceChildren(frameworksInUse)
    : [];
  if (complianceChildren.length > 0) {
    // No url: the row is a toggle for the frameworks beneath it, the same as
    // the SocialPro row above and the project sidebar's Compliance row.
    children.push({
      children: complianceChildren,
      icon: <Icon name="compliance" />,
      id: "org-compliance",
      title: "Compliance",
      type: "item"
    });
  }

  const nav = {
    children,
    id: "insights",
    title: "Analytics",
    type: "group"
  };

  return nav;
};

/**
 * The "View" group (id `library`) holds what an organisation AUTHORS once and
 * reuses across projects — its exports, policies and frameworks, its document
 * library — beside the admin-gated SROI Builder, now that the organisation-
 * level Smart Directory is gone (the directory is project-scoped — see
 * `.scratch/smart-directory-org-removal/HANDOFF.md`).
 *
 * Captioned "View" to match the project sidebar's group of the same name
 * (Export Generator, Smart Directory), so the two levels read alike; the id
 * keeps its history. The Export Generator leads the list. Policies &
 * Frameworks is offered to everyone, not only admins: org admins author,
 * everyone reads (the lambda enforces the write side), and the read-only
 * Library page is exactly what a non-admin gets.
 *
 * A childless group is dropped by `MainSidebarMenu` (`rendersRows`) — heading
 * included. `MainSidebarMenuTree` alone was not enough: it returns null for the
 * rows, but the caption is rendered above it, so a new account saw "LIBRARY"
 * over blank space. That still guards the Insights group above, which is empty
 * until the visibility flags land.
 */
const getLibraryNav = (
  isSocialProAdmin: boolean,
  isContractor: boolean
): NavItemType => {
  const nav = {
    children: [
      // Exports across the organisation's projects lead the group.
      ...(isSocialProAdmin
        ? [
            {
              dataTestId: "organisation-export-generator",
              icon: <Icon name="download" />,
              id: "organisation-export-generator",
              title: "Export Generator",
              type: "item",
              url: RouteHelper.organisationExportGenerator
            }
          ]
        : []),
      // Framework authoring belongs to whoever can SET UP projects — the
      // contractor flag, the same gate project creation has. A subcontractor
      // organisation reports against frameworks; it does not write them.
      ...(isContractor
        ? [
            {
              dataTestId: "framework-library",
              icon: <Icon name="file-check" />,
              id: "framework-library",
              title: "Policies & Frameworks",
              type: "item",
              url: RouteHelper.frameworkLibrary
            }
          ]
        : []),
      {
        dataTestId: "organisation-documents",
        icon: <Icon name="file" />,
        id: "organisation-documents",
        title: "Document Register",
        type: "item",
        url: RouteHelper.organisationDocumentsLevel1
      },
      ...(isSocialProAdmin
        ? [
            {
              badge: "Beta",
              dataTestId: "sroi-builder",
              icon: <Icon name="trending-up" />,
              id: "sroi-builder",
              title: "SROI Builder",
              type: "item",
              url: RouteHelper.sroiBuilder
            }
          ]
        : [])
    ],
    // The id stays "library": `resolveSelectedItem` and the skeleton read it.
    id: "library",
    title: "View",
    type: "group"
  };

  return nav;
};

export const getNavMenuItems = (
  isSocialProAdmin: boolean,
  sectionVisibility?: OrgSectionVisibilityModel,
  frameworksInUse: OrganisationFrameworkSummaryLineItem[] = [],
  isContractor = false,
  projectCount?: number
): NavItemListType => {
  const items = [
    getManageNav(projectCount),
    getInsightsNav(sectionVisibility, frameworksInUse, isContractor),
    getLibraryNav(isSocialProAdmin, isContractor)
  ];

  return { items };
};
